import { Injectable, Injector, NgZone } from "@angular/core";
import { collection, doc, Firestore, getDocs, onSnapshot, orderBy, query, where } from "@angular/fire/firestore";
import { MatDialog } from "@angular/material/dialog";
import { Data, Router } from "@angular/router";
import { AuthService } from "@core/auth.service";
import { AuthGamesService } from "@core/AuthServices/authGames/auth-games.service";
import { AuthStateService } from "@core/auth-state.service";
import { ModalController } from "@ionic/angular";
import { BehaviorSubject, EMPTY, from, Observable, of, Subscription } from "rxjs";
import { catchError, map, shareReplay, switchMap, take } from "rxjs/operators";
import { BaseService } from "../app.base.service";
import { VoucherDialogComponent } from "../components/voucher-dialog/voucher-dialog.component";
import { StorePaymentComponent } from "../pages/store/store-payment/store-payment.component";
import { CommonService } from "./common/common.service";
import { GTMSegmentService } from "./gtm-segment.service";
import { PaymentService } from "./payment.service";
import { RequestUserService } from "./RequestBodyService/RequestUser/request-user.service";

@Injectable({
  providedIn: "root",
})
export class VoucherService extends BaseService {
  public voucher$ = new BehaviorSubject({});
  private readonly voucherWalletSnapshot$ = new BehaviorSubject<Data[]>([]);
  private voucherWalletListenerSub?: Subscription;

  getVoucherList(): Observable<Data[]> {
    this.ensureVoucherWalletListener();
    return this.voucherWalletSnapshot$.asObservable();
  }

  getVoucherWalletSnapshot(): Data[] {
    return this.voucherWalletSnapshot$.getValue();
  }

  private ensureVoucherWalletListener(): void {
    if (this.voucherWalletListenerSub) {
      return;
    }

    this.voucherWalletListenerSub = this.commonService.getUid("VoucherService").pipe(
      take(1),
      switchMap((userId) => {
        if (!userId) {
          this.voucherWalletSnapshot$.next([]);
          return EMPTY;
        }

        const collectionRef = collection(this.firestore, `/users/${userId}/voucher_wallet`);
        const q = query(
          collectionRef,
          where("status", "==", true),
          orderBy("expiry_date", "asc")
        );

        // Defense-in-depth gate for user-private voucher wallet (LOTTOSOCIAL-PWA-1MEE)
        return this.authStateService.customTokenReady$.pipe(
          take(1),
          switchMap(() => new Observable<Data[]>((observer) => {
            const unsubscribe = onSnapshot(
              q,
              (snapshot) => {
                const vouchers = snapshot.docs.map((doc) => ({
                  id: doc.id,
                  ...doc.data(),
                })) as Data[];

                this.ngZone.run(() => {
                  this.voucherWalletSnapshot$.next(vouchers);
                });
                observer.next(vouchers);
              },
              (error) => observer.error(error)
            );

            return () => unsubscribe();
          }))
        );
      })
    ).subscribe({
      error: (error) => {
        console.error("[VoucherService] voucher wallet listener failed:", error);
        this.voucherWalletSnapshot$.next([]);
      },
    });
  }

  public redeemVoucher(voucherCode: string, document, syndicatePromotion, additionalAwardId: string | number = "") {
    return this.RequestUserService.getRedeemUserVoucherCodeRequest(
      voucherCode,
      localStorage.getItem('phoneNumber'),
      localStorage.getItem('prosub_id'),
      document,
      syndicatePromotion,
      additionalAwardId,
    ).pipe(
      switchMap(requestBody => this.authService?.getV2CloudFunction("validatevoucherapiv2", requestBody)),
      map((data: any) => {
        // Ensure we always return a valid response object with status field
        const response = data?.response ? data?.response : data;

        // If response is null or undefined, return a structured error response
        if (!response) {
          return {
            status: 'rejected',
            error_code: '1026',
            message: 'Invalid response from server'
          };
        }

        // If response doesn't have a status field, add one
        if (!response.status) {
          return {
            ...response,
            status: 'rejected',
            error_code: response.error_code || '1026',
            message: response.message || 'Unknown error occurred'
          };
        }

        return response;
      }),
      catchError((error) => {
        // Log the error for debugging
        console.error('[VoucherService] Error in voucher redemption:', error);

        // Return a structured error response as an Observable
        // Always ensure the response has a status field
        return of({
          status: 'rejected',
          error_code: error?.code || error?.error_code || '1026', // Default error code
          message: error?.message || 'Network error occurred while redeeming voucher'
        });
      })
    );
  }

  hideInvalidVoucher(voucherId: string): Observable<void | Error> {
    return this.commonService.getUid("VoucherService").pipe(
      switchMap((userId) => {
        if (!userId || !voucherId) {
          // Log warning for debugging but don't throw error - handle gracefully
          console.warn('[VoucherService] hideInvalidVoucher: Missing userId or voucherId', {
            userId: userId ? 'present' : 'missing',
            voucherId: voucherId ? 'present' : 'missing'
          });
          return EMPTY; // Return empty observable instead of throwing error
        }

        const path_id = "jsnfpO0nL2cb3Os9jVIQ";
        const dv = { docId: voucherId };
        const doc_data = { status: false };
        const method = "update";

        return from(this.authService.getV2CloudFunction("updatefirestoredoc", {
          path_id,
          doc_data,
          dv,
          method,
        })).pipe(
          catchError((err) => of(err)) // return error as observable instead of throwing
        );
      })
    );
  }

  voucherRedeemed(voucherId: string): Observable<void | Error> {
    return this.commonService.getUid("VoucherService").pipe(
      switchMap((userId) => {
        if (!userId || !voucherId) {
          // Log warning for debugging but don't throw error - handle gracefully
          console.warn('[VoucherService] voucherRedeemed: Missing userId or voucherId', {
            userId: userId ? 'present' : 'missing',
            voucherId: voucherId ? 'present' : 'missing'
          });
          return EMPTY; // Return empty observable instead of throwing error
        }
        const path_id = "jsnfpO0nL2cb3Os9jVIQ";
        const dv = { docId: voucherId };
        const doc_data = { isRedeemed: true };
        const method = "update";

        return from(this.authService.getV2CloudFunction("updatefirestoredoc", {
          path_id,
          doc_data,
          dv,
          method,
        })).pipe(
          catchError((err) => of(err)) // emit error instead of breaking stream
        );
      })
    );
  }


  setSyndicatePending(): Observable<void | Error> {
    return this.commonService.getUid("VoucherService").pipe(
      switchMap((userId) => {
        if (!userId) {
          // Log warning for debugging but don't throw error - handle gracefully
          console.warn('[VoucherService] setSyndicatePending: Missing userId', {
            userId: userId ? 'present' : 'missing'
          });
          return EMPTY; // Return empty observable instead of throwing error
        }

        const path_id = "yzF1Ss5eiMqnh91zQMtv";
        const doc_data = { syndicate_pending: true };
        const method = "update";

        return from(this.authService.getV2CloudFunction("updatefirestoredoc", {
          path_id,
          doc_data,
          method,
        })).pipe(
          catchError((err) => of(err)) // handle errors gracefully
        );
      })
    );
  }


  constructor(
    private firestore: Firestore,
    private injector: Injector,
    private dialog: MatDialog,
    private modalController: ModalController,
    private paymentService: PaymentService,
    private authService: AuthService,
    private router: Router,
    private RequestUserService: RequestUserService,
    private AuthGamesService: AuthGamesService,
    private gtmService: GTMSegmentService,
    private commonService: CommonService,
    private ngZone: NgZone,
    private authStateService: AuthStateService
  ) {
    super(injector);
  }

  public async onVoucherSuccessClaim(dialogData: Data) {
    // Validate dialogData before opening the dialog
    if (!dialogData || typeof dialogData !== 'object') {
      console.error('[onVoucherSuccessClaim] Invalid dialog data received:', dialogData);
      // Show error toast to user
      this.authService.getConstantsMsg('1026').then((translatedMsg) => {
        const defaultMsg = {
          message: { line_1: 'Something went wrong. Please try again', line_2: '' },
          style: { icon: 'tick circle error.svg', type: 'Error' },
        };
        this.authService.showToastMessage(translatedMsg, defaultMsg);
      });
      return;
    }

    const validVoucherDialog = this.dialog.open(VoucherDialogComponent, {
      width: "38.91vh",
      backdropClass: "back-drop-color",
      panelClass: "my-voucher-panel",
      data: dialogData,
    });
    try {
      validVoucherDialog.afterClosed()?.subscribe(
        (dialogResponse: boolean) => {
          if (dialogResponse) {
            if (dialogData?.button_link && dialogData?.button_link.search("store") > -1) {
              this.openStorePage();
            } else if (
              dialogData?.button_link &&
              dialogData?.button_link.search("ticket") > -1 &&
              dialogData?.syndicateId
            ) {
              this.openTicketPage(dialogData.syndicateId);
            } else if (dialogData?.button_link && dialogData?.button_link.search("game") > -1) {
              this.openGamePage();
            } else if (dialogData?.button_link && dialogData?.button_link.search("payment") > -1) {
              this.makePayment(dialogData);
            } else if (dialogData?.button_link && dialogData?.button_link.search("syndicate") > -1) {
              this.setSyndicatePending().pipe(take(1)).subscribe({
                next: () => this.router.navigateByUrl('mainTabs/tabs/syndicate'),
                error: err => {/* handle */}
              });
            }
          }
        },
        (err) => {
          return err;
        }
      );
    } catch (error) {
      return error;
    }
  }

  async makePayment(offer: Data) {
    const openPaymentModal = await this.modalController.create({
      component: StorePaymentComponent,
      componentProps: {
        data: this.formPaymentData(offer),
      },
    });

    openPaymentModal.onDidDismiss().then((res) => {
      try {
        if (res?.data && res?.data?.syndicateId) {
          this.paymentService
            .getSyndicateInfoWithProducts(res?.data?.syndicateId)
            .pipe(
              map((detail: Data) => {
                if (!detail?.[0]?.data()) return;
                const syndicateInfo = detail?.[0]?.data();
                syndicateInfo["product"] = [];
                const productDocs = detail && detail?.[1] && detail?.[1].docs ? detail[1].docs : "";
                for (let index = 0; index < productDocs?.length; index++) {
                  syndicateInfo?.product?.push(productDocs?.[index]?.data());
                }
                return syndicateInfo;
              })
            )
            ?.subscribe(
              (syndicateInfo) => {
                this.showTicket(syndicateInfo);
              },
              (err) => {
                throw err;
              }
            );
        }
      } catch (error) {
        return error;
      }
    }).catch((err) => {
      return err;
    });

    return await openPaymentModal?.present();
  }

  formPaymentData(offer: Data) {
    return {
      prize: offer?.price,
      draw: "",
      game: offer?.product_name,
      offers: {
        offerid: offer?.product_id,
        description: offer?.product_description,
        price: offer?.price,
      },
      real_name: offer?.product_name,
      isCreatingPS: false,
      voucher_code_id: offer?.voucher_code_id,
      page: "voucher",
    };
  }

  private async showTicket(syndicateInfo) {
    const { SyndicateTicketsComponent } = await import("../pages/syndicate/syndicate-tickets/syndicate-tickets.component");
    const syndicateTickets = await this.modalController.create({
      component: SyndicateTicketsComponent,
      componentProps: { syndicate: syndicateInfo },
      cssClass: "modal-fullScreen",
      //enterAnimation: CustomAnimation.enterFromRight,
      //leaveAnimation: CustomAnimation.exitThroughRight,
    });
    return await syndicateTickets.present();
  }

  private async openTicketPage(syndicateId) {
    const { SyndicateTicketsComponent } = await import("../pages/syndicate/syndicate-tickets/syndicate-tickets.component");
    const syndicateTickets = await this.modalController.create({
      component: SyndicateTicketsComponent,
      componentProps: { syndicate: { syndicate_id: syndicateId, syndicate_name: "", syndicate_type: "" } },
      cssClass: "modal-fullScreen",
      // enterAnimation: CustomAnimation.enterFromRight,
      //  leaveAnimation: CustomAnimation.exitThroughRight,
    });
    return await syndicateTickets.present();
  }

  openStorePage() {
    this.router.navigateByUrl("mainTabs/tabs/store");
  }

  openSyndicatePage() {
    this.router.navigateByUrl("mainTabs/tabs/syndicate");
  }

  openGamePage() {
    this.router.navigateByUrl("mainTabs/tabs/game");
  }

  getVoucherLookupData(voucherId: string) {
    const collectionRef = collection(this.firestore, "/general_modules/discount_voucher_lookup/list");
    const q = query(collectionRef, where("voucher_code", "==", voucherId));

    return from(getDocs(q)).pipe(
      map((querySnapshot) => {
        return querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
      })
    );
  }

  updateDiscountVoucherHideStatus(voucherId: string, status: boolean): void {
    this.commonService.getUid("VoucherService").pipe(
      switchMap((userId) => {
        if (!userId || !voucherId) {
          // Log warning for debugging but don't throw error - handle gracefully
          console.warn('[VoucherService] updateDiscountVoucherHideStatus: Missing userId or voucherId', {
            userId: userId ? 'present' : 'missing',
            voucherId: voucherId ? 'present' : 'missing'
          });
          return EMPTY; // Return empty observable instead of throwing error
        }

        const path_id = "jsnfpO0nL2cb3Os9jVIQ";
        const dv = { docId: voucherId };
        const doc_data = { hide: status };
        const method = "update";

        return from(this.authService.getV2CloudFunction("updatefirestoredoc", {
          path_id,
          doc_data,
          dv,
          method,
        })).pipe(
          catchError((err) => of(err)) // return error inside observable
        );
      })
    ).subscribe({
      error: (err) => {
        // Log any unexpected errors but don't propagate to Sentry
        console.error('[VoucherService] updateDiscountVoucherHideStatus: Unexpected error', err);
      }
    });
  }


  getVoucherPageData(): Observable<any | null> {
    return this.commonService.getUid("VoucherService").pipe(
      switchMap((userId) => {
        if (!userId) {
          return of(null);
        }

        const documentRef = doc(this.firestore, `users/${userId}/main_pages/voucher`);

        // Defense-in-depth gate for user-private voucher page (LOTTOSOCIAL-PWA-1MEE)
        return this.authStateService.customTokenReady$.pipe(
          take(1),
          switchMap(() => new Observable<any>((observer) => {
            const unsubscribe = onSnapshot(
              documentRef,
              (docSnapshot) => {
                observer.next(docSnapshot.data() ?? null);
              },
              (error) => observer.error(error)
            );
            return () => unsubscribe();
          }))
        );
      }),
      catchError((error) => {
        console.error("Error fetching voucher page data:", error);
        return of(null);
      }),
      shareReplay(1) // cache last value for multiple subscribers
    );
  }


  openTypeFormForFeedback(notification: Data, language: string): Observable<void> {
    return this.commonService.getUid("VoucherService").pipe(
      switchMap((userId) => {
        if (!userId) {
          return of(null);
        }

        // Only proceed if it's a nav action, not viewed, and has a Typeform URL
        if (
          notification?.action?.type !== "nav" ||
          notification?.viewed ||
          !notification?.action?.details?.includes("typeform.com")
        ) {
          return of(null);
        }

        const balance$ = this.authService
          .getAngularFireStore(`/users/${userId}/balance/current`, "get")
          .pipe(map((res: Data) => res?.data()));

        return balance$.pipe(
          map((balance: Data) => {
            if (balance) {
              const credit_balance = balance?.preferred_currency?.credit || null;
              const Point_balance = balance?.points || null;

              const hiddenData = {
                user_id: userId,
                Point_balance,
                Credit_Balance: credit_balance,
                Language: language || "en",
              };

              const url = notification?.action?.details;
              this.AuthGamesService.typeForm(url, hiddenData, notification);
            }
          })
        );
      }),
      catchError((err) => {
        console.error("Error opening Typeform for feedback:", err);
        return of(void 0);
      })
    );
  }

  public showInvalidVoucherToast() {
    this.authService
      .getConstantsMsg('1150')
      .then((translatedMsg) => {
        const defaultMsg = {
          message: {
            line_1: "Discount voucher is invalid!",
            line_2: "",
          },
          style: {
            icon: "tick circle error.svg",
            type: "Error",
          },
        };
        this.authService.showToastMessage(translatedMsg, defaultMsg);
      })
      .catch((error) => {
        return error;
      });
  }


  updateDiscountVoucherRedeemedStatus(vouchersData: Data, status: boolean): void {
    this.commonService.getUid("VoucherService").pipe(
      switchMap((userId) => {
        const voucherId = vouchersData?.id;
        if (!userId || !voucherId) {
          // Log warning for debugging but don't throw error - handle gracefully
          console.warn('[VoucherService] updateDiscountVoucherRedeemedStatus: Missing userId or voucherId', {
            userId: userId ? 'present' : 'missing',
            voucherId: voucherId ? 'present' : 'missing',
            voucherData: vouchersData
          });
          return EMPTY; // Return empty observable instead of throwing error
        }

        // side effect: log/event before updating Firestore
        this.userRedeemedDiscountVoucherEvent(vouchersData, userId);

        const path_id = "jsnfpO0nL2cb3Os9jVIQ";
        const dv = { docId: voucherId };
        const doc_data = { isRedeemed: status };
        const method = "update";

        return from(this.authService.getV2CloudFunction("updatefirestoredoc", {
          path_id,
          doc_data,
          dv,
          method,
        })).pipe(
          catchError((err) => of(err)) // return error inside observable
        );
      })
    ).subscribe({
      error: (err) => {
        // Log any unexpected errors but don't propagate to Sentry
        console.error('[VoucherService] updateDiscountVoucherRedeemedStatus: Unexpected error', err);
      }
    });
  }


  userRedeemedDiscountVoucherEvent(vouchersData: Data, userId: string) {
    const mixpanelProperties = {
      Batch_ID: vouchersData?.voucher_code_id,
      id: userId,
      Supplier: vouchersData?.supplier,
      Template_Code: vouchersData?.voucher_code,
      Voucher_Name: vouchersData?.voucher_name,
      User_State: localStorage.getItem("user_state"),
      flash_sale: vouchersData?.flash_sale ?? false,
    }
    this.gtmService.mixpanleEventInitializer("User_Redeemed_Discount_Voucher", mixpanelProperties);
  }

}
